import { useCallback } from 'react';
import { doc, updateDoc, deleteDoc, getDoc, Timestamp } from 'firebase/firestore';
import { BookingStatus, ReservationTag } from '../types/booking';
import { useRestaurantDatabase } from '../context/RestaurantDatabaseContext';

export function useBookingActions() {
  const { database, hotelId } = useRestaurantDatabase();

  const updateBookingStatus = useCallback(async (
    bookingId: string,
    newStatus: BookingStatus,
    cancelReason?: string
  ) => {
    console.log('[useBookingActions] updateBookingStatus called:', { bookingId, newStatus, hotelId });

    if (!database || !hotelId) {
      console.error('[useBookingActions] Missing database or hotelId:', { database: !!database, hotelId });
      throw new Error('Database not initialized');
    }

    const bookingPath = `hotels/${hotelId}/bookings`;
    console.log('[useBookingActions] Booking path:', bookingPath, 'bookingId:', bookingId);

    const bookingRef = doc(database, bookingPath, bookingId);

    const updateData: Record<string, any> = {
      status: newStatus,
      updatedAt: Timestamp.now(),
    };

    // Add status-specific timestamps
    switch (newStatus) {
      case 'confirmed':
        updateData.confirmedAt = Timestamp.now();
        break;
      case 'seated':
        updateData.seatedAt = Timestamp.now();
        break;
      case 'completed':
        updateData.completedAt = Timestamp.now();
        break;
      case 'cancelled':
        updateData.cancelledAt = Timestamp.now();
        if (cancelReason) {
          updateData.cancelReason = cancelReason;
        }
        break;
    }

    console.log('[useBookingActions] Updating document with data:', updateData);
    await updateDoc(bookingRef, updateData);
    console.log('[useBookingActions] Update successful');
  }, [database, hotelId]);

  const confirmBooking = useCallback(async (bookingId: string) => {
    await updateBookingStatus(bookingId, 'confirmed');
  }, [updateBookingStatus]);

  const seatGuest = useCallback(async (bookingId: string) => {
    await updateBookingStatus(bookingId, 'seated');
  }, [updateBookingStatus]);

  const completeBooking = useCallback(async (bookingId: string) => {
    await updateBookingStatus(bookingId, 'completed');
  }, [updateBookingStatus]);

  const cancelBooking = useCallback(async (bookingId: string, reason?: string) => {
    await updateBookingStatus(bookingId, 'cancelled', reason);
  }, [updateBookingStatus]);

  const markNoShow = useCallback(async (bookingId: string) => {
    const bookingRef = doc(database, `hotels/${hotelId}/bookings`, bookingId);
    // Read current tags to append 'no-show' without overwriting
    const snap = await getDoc(bookingRef);
    const currentTags: ReservationTag[] = snap.exists() ? (snap.data().tags || []) : [];
    const updatedTags = currentTags.includes('no-show') ? currentTags : [...currentTags, 'no-show' as ReservationTag];
    await updateDoc(bookingRef, {
      status: 'no-show' as BookingStatus,
      tags: updatedTags,
      updatedAt: Timestamp.now(),
    });
  }, [database, hotelId]);

  const assignTable = useCallback(async (
    bookingId: string,
    tableId: string,
    tableName: string
  ) => {
    const bookingRef = doc(database, `hotels/${hotelId}/bookings`, bookingId);
    await updateDoc(bookingRef, {
      tableId,
      tableName,
      updatedAt: Timestamp.now(),
    });
  }, [database, hotelId]);

  const updateBooking = useCallback(async (
    bookingId: string,
    data: Record<string, any>
  ) => {
    if (!database || !hotelId) {
      throw new Error('Database not initialized');
    }
    const bookingRef = doc(database, `hotels/${hotelId}/bookings`, bookingId);
    await updateDoc(bookingRef, { ...data, updatedAt: Timestamp.now() });
  }, [database, hotelId]);

  const deleteBooking = useCallback(async (bookingId: string) => {
    console.log('[useBookingActions] deleteBooking called:', { bookingId, hotelId });

    if (!database || !hotelId) {
      console.error('[useBookingActions] Missing database or hotelId:', { database: !!database, hotelId });
      throw new Error('Database not initialized');
    }

    const bookingPath = `hotels/${hotelId}/bookings`;
    console.log('[useBookingActions] Deleting from path:', bookingPath, 'bookingId:', bookingId);

    const bookingRef = doc(database, bookingPath, bookingId);
    await deleteDoc(bookingRef);
    console.log('[useBookingActions] Delete successful');
  }, [database, hotelId]);

  return {
    updateBookingStatus,
    updateBooking,
    confirmBooking,
    seatGuest,
    completeBooking,
    cancelBooking,
    markNoShow,
    assignTable,
    deleteBooking,
  };
}
